// Call prepare/step1 with the first workspace audience id and render the form using MultiStepFlowFormv2

import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import { AudienceRequiredEmptyState } from '@/features/audiences/components/AudienceRequiredEmptyState';
import ErrorBoundary from '@/components/ErrorBoundary';
import { Loading } from '@/components/loaders';
import { MultiStepFlowForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/MultiStepFlowFormv2';
import { FlowFormData, FlowMappingsConfig } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import { FlowMappingsForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/FlowMappingsForm';
import { hasMappingsUi } from '@/features/connectors/flows';
import { useWhatsappConnectorFlowRunner } from '@/features/connectors/whatsapp';
import { StepPageActions } from '@/global/components/Layout/StepPageActions';
import { StepPageActionsButton } from '@/global/components/Layout/StepPageActions/types';
import { showToast } from '@/features/toasts/toastsSlice';
import { useCompanyValues } from '@/providers/CompanyProvider';
import { useAppDispatch } from '@/reducers';
import { ToastType } from '@/models/toast';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { RouteConstants } from '@/features/router/RouteConstants';

export const WhatsappTemplateWorkaround = () => {
  const { audienceId, isLoading: isAudienceLoading, isAudienceSettled, hasAudience } = useFirstAudienceId();
  const { currentLanguage } = useCompanyValues();
  const appDispatch = useAppDispatch();
  const { t } = useTranslation();
  const history = useHistory();

  const {
    prepareResult,
    prepareError,
    isPreparePending,
    createInstance,
    connectorFlowId,
    connectorInstanceId,
    isChannelLoading,
  } = useWhatsappConnectorFlowRunner('templateInsert', {
    audienceId,
    lang: currentLanguage,
  });

  const [formData, setFormData] = useState<FlowFormData>({});
  const [multiStepMappingsConfig, setMultiStepMappingsConfig] = useState<FlowMappingsConfig | null>(null);
  const [mappingData, setMappingData] = useState<FlowFormData>({});
  const [isMappingsFormValid, setIsMappingsFormValid] = useState(false);
  const [multiStepsCompleted, setMultiStepsCompleted] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  type CreateTemplateOpts = {
    flowParams?: FlowFormData;
    flowMappings?: FlowFormData;
    /** When true, skip mappings-form validation (flow finished without a mappings UI). */
    fromFlowComplete?: boolean;
  };

  const handleCreateTemplate = async (opts?: CreateTemplateOpts) => {
    if (isSubmitting) {
      return { success: false, message: 'Already submitting' };
    }

    const flowParams = opts?.flowParams ?? formData;
    const flowMappings = opts?.flowMappings ?? mappingData;

    setCreateError(null);

    if (!opts?.fromFlowComplete && hasMappingsUi(multiStepMappingsConfig) && !isMappingsFormValid) {
      return { success: false, message: 'Invalid mappings' };
    }

    if (!connectorInstanceId) {
      const message = String(t('campaign:noConnectorInstanceFound'));
      setCreateError(message);
      appDispatch(showToast({ type: ToastType.ERROR, title: message }));
      return { success: false, message };
    }

    if (!audienceId) {
      const message = String(t('campaign:missingAudience.toast'));
      setCreateError(message);
      appDispatch(showToast({ type: ToastType.ERROR, title: message }));
      return { success: false, message };
    }

    setIsSubmitting(true);
    try {
      const result = await createInstance(flowParams, flowMappings);
      if (!result.success) {
        const errorMessage = result.message ?? String(t('campaign:flowInstanceCreationFailed'));
        setCreateError(errorMessage);
        appDispatch(showToast({ type: ToastType.ERROR, title: errorMessage }));
        return { success: false, message: errorMessage };
      }

      appDispatch(showToast({ title: String(t('campaign:whatsappTemplates.createSuccess')), type: ToastType.SUCCESS }));
      history.push(RouteConstants.whatsappTemplates);
      return { success: true };
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelButton: StepPageActionsButton = {
    text: 'COMMON.cancel' as any,
    onClick: () => history.push(RouteConstants.whatsappTemplates),
  };

  const nextButton: StepPageActionsButton = {
    text: 'COMMON.confirm' as any,
    onClick: () => void handleCreateTemplate(),
    disabled: !isMappingsFormValid || isSubmitting,
  };

  const handleMappingDataChange = useCallback((data: FlowFormData) => {
    setMappingData(data);
  }, []);

  const handleMappingsValidationChange = useCallback((valid: boolean) => {
    setIsMappingsFormValid(valid);
  }, []);

  const handleFlowComplete = async (allParams: FlowFormData, mappingsConfig: FlowMappingsConfig) => {
    setFormData(allParams);
    setMultiStepMappingsConfig(mappingsConfig);
    setMappingData({});
    setIsMappingsFormValid(false);
    setCreateError(null);

    if (hasMappingsUi(mappingsConfig)) {
      setMultiStepsCompleted(true);
      return;
    }

    // Keep the multi-step form mounted until create succeeds (redirect unmounts the page).
    await handleCreateTemplate({ flowParams: allParams, flowMappings: {}, fromFlowComplete: true });
  };

  const showMappingsStep = hasMappingsUi(multiStepMappingsConfig);
  const isTemplateLoading = isAudienceLoading || isChannelLoading || (hasAudience && isPreparePending);

  if (isAudienceLoading) {
    return (
      <div className='space-y-4'>
        <div>
          <h2 className='text-3xl font-semibold text-gray-900'>{t('campaign:whatsappTemplates.createWhatsappTemplate')}</h2>
        </div>
        <Loading />
      </div>
    );
  }

  if (isAudienceSettled && !hasAudience) {
    return (
      <div className='space-y-4'>
        <div>
          <h2 className='text-3xl font-semibold text-gray-900'>{t('campaign:whatsappTemplates.createWhatsappTemplate')}</h2>
        </div>
        <AudienceRequiredEmptyState />
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <div>
        <h2 className='text-3xl font-semibold text-gray-900'>{t('campaign:whatsappTemplates.createWhatsappTemplate')}</h2>
      </div>
      {createError ? <div className='rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700'>{createError}</div> : null}
      {!multiStepsCompleted && (
        <ErrorBoundary>
          {prepareResult ? (
            <MultiStepFlowForm
              key={`${connectorFlowId}-${audienceId}`}
              id='template-workaround-form'
              showTemplatePreview
              audienceId={audienceId!}
              connectorInstanceId={connectorInstanceId!}
              connectorFlowId={connectorFlowId!}
              paramsConfig={prepareResult}
              isInAction={isSubmitting}
              onComplete={(allParams, mappingsConfig) => {
                void handleFlowComplete(allParams, mappingsConfig);
              }}
            />
          ) : (
            <p className='text-sm text-gray-600'>
              {isTemplateLoading && !prepareError
                ? t('campaign:whatsappTemplates.templateLoadingMessage')
                : t('campaign:whatsappTemplates.failedToLoadTemplate')}
            </p>
          )}
        </ErrorBoundary>
      )}
      {multiStepsCompleted && showMappingsStep ? (
        <ErrorBoundary>
          <div>
            <h3 className='mb-4 text-lg font-medium'>{t('ALL:destinations.steps.configureFlow.dataMapping')}</h3>
            <FlowMappingsForm
              config={multiStepMappingsConfig}
              formData={mappingData}
              onFormDataChange={handleMappingDataChange}
              onValidationChange={handleMappingsValidationChange}
            />
            <StepPageActions primaryButton={nextButton} secondaryButton={cancelButton} />
          </div>
        </ErrorBoundary>
      ) : null}
    </div>
  );
};
